import { useEffect, useState } from "react";
import { Button } from "@knowtex-ai/components";
import { Copy, Send } from "iconoir-react";
import { useToast } from "../../hooks/use-toast";
import { buildEmailDraft } from "./foiaUtils";
import { postFoiaAction } from "./api";
import { STEP_KEY_TO_PREPARED_ACTION_CODE } from "./foiaActionCodes";

export default function EmailDraftCard({ request, stepKey, onSent }) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const draft = buildEmailDraft(request, stepKey);

  useEffect(() => {
    const actionCode = STEP_KEY_TO_PREPARED_ACTION_CODE[stepKey];
    if (!actionCode) return;
    // Recording that a draft was generated is informational — it never
    // blocks rendering the draft and is never surfaced to Cortney, so
    // failures (including a repeat already-done) are only logged.
    postFoiaAction(request.id, { actionCode }).catch((err) => {
      console.error(`Failed to record ${actionCode}:`, err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id, stepKey]);

  const copyDraft = async () => {
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied to clipboard", description: "Paste it into your email client to send it." });
    } catch {
      toast({ title: "Select the text above", description: "Your browser blocked automatic copying." });
    }
  };

  const markSent = async () => {
    setSending(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      await onSent();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="foia-email-draft">
      <p className="text-sm text-gray-600 mb-3">
        The app assembled this draft. Review it, then send it yourself from your email client — the app never sends
        email on your behalf.
      </p>

      <div className="foia-email-draft__field">
        <div className="foia-email-draft__label">Subject</div>
        <div className="foia-email-draft__subject">{draft.subject}</div>
      </div>
      <div className="foia-email-draft__field">
        <div className="foia-email-draft__label">Body</div>
        <div className="foia-email-draft__body">{draft.body}</div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="secondary" icon={Copy} onClick={copyDraft}>
          Copy
        </Button>
        <Button icon={Send} loading={sending} onClick={markSent}>
          I sent this email
        </Button>
      </div>
    </div>
  );
}
